// Real-time task sync client. Opens a WebSocket to the TaskHub Durable Object
// (authed via a short-lived ticket) and, on a 'tasks-changed' signal, asks the
// app to refetch /tasks. Multi-user edits then appear in ~1s instead of only
// after a manual reload.
//
//   connectTaskSync(onChanged)  — WS lifecycle: ticket → connect → signal →
//     onChanged; capped+jittered reconnect; refetch on every (re)connect so a
//     signal missed while offline is recovered; keepalive ping (DO auto-pongs
//     without waking). Returns { close }.
//   reconcileTasks(current, fresh) — apply server truth without churn: drop
//     gone ids, add new, replace changed rows (updatedAt fast-path + JSON
//     fallback), preserve identity for unchanged rows. Same ref if nothing
//     changed → React skips the render.
//   createSyncScheduler(refetch) — defers a refetch while the local user has a
//     mutation in flight, so a remote signal can't clobber an in-flight
//     optimistic edit. A 15s safety timeout prevents a hung request from
//     starving remote updates forever.

import * as api from './api.ts';

const MAX_BACKOFF_MS = 30_000;
const PING_MS = 30_000;
const INFLIGHT_SAFETY_MS = 15_000;

// onStatus(state) is optional: 'connecting' | 'online' | 'offline'. It drives the
// sidebar connection indicator so staff can tell when the live feed is down (and
// not trust a frozen screen). A brief drop shows 'connecting'; once reconnects
// have failed a few times (OFFLINE_AFTER attempts, ~a few seconds) it escalates
// to 'offline' + the top bar, so momentary blips don't cry wolf but a real outage
// is loud.
const OFFLINE_AFTER = 2;
export function connectTaskSync(onChanged, onStatus) {
  let ws = null;
  let closed = false;
  let attempt = 0;
  let pingTimer = null;
  let reconnectTimer = null;
  const status = (s) => { try { onStatus && onStatus(s); } catch { /* host error is not ours */ } };

  function scheduleReconnect() {
    if (closed) return;
    status(attempt >= OFFLINE_AFTER ? 'offline' : 'connecting');   // brief drop → connecting; sustained → offline
    const ceiling = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);   // 1s,2s,4s…30s
    const delay = ceiling / 2 + Math.random() * (ceiling / 2);        // jitter (Codex #3)
    attempt += 1;
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, delay);
  }

  async function connect() {
    if (closed) return;
    if (attempt === 0) status('connecting');
    let ticket;
    try { ticket = (await api.fetchWsTicket()).ticket; }
    catch { scheduleReconnect(); return; }
    if (closed) return;
    try { ws = new WebSocket(api.taskWsUrl(ticket)); }
    catch { scheduleReconnect(); return; }

    ws.onopen = () => {
      attempt = 0;
      status('online');
      onChanged();   // recover any signal missed while disconnected
      clearInterval(pingTimer);
      pingTimer = setInterval(() => { try { ws && ws.send('ping'); } catch { /* closing */ } }, PING_MS);
    };
    ws.onmessage = (e) => {
      let msg = null;
      try { msg = JSON.parse(e.data); } catch { return; }   // 'pong' keepalive → ignore
      if (msg && msg.type === 'tasks-changed') onChanged();
    };
    ws.onclose = () => { clearInterval(pingTimer); if (!closed) scheduleReconnect(); };
    ws.onerror = () => { try { ws && ws.close(); } catch { /* already closing */ } };
  }

  connect();
  return {
    close() {
      closed = true;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      try { ws && ws.close(); } catch { /* already closing */ }
    }
  };
}

// Pure: reconcile the local task array against fresh server truth. Returns the
// SAME `current` reference when nothing changed so React can bail out of render.
export function reconcileTasks(current, fresh) {
  const curById = new Map(current.map(t => [t.id, t]));
  const freshIds = new Set(fresh.map(t => t.id));
  let changed = current.length !== fresh.length;
  const next = fresh.map(f => {
    const cur = curById.get(f.id);
    // unchanged: same updatedAt (fast path) AND byte-identical (catches a child-
    // table edit that somehow didn't bump updatedAt — D6 / Codex #7 belt+braces)
    if (cur && cur.updatedAt === f.updatedAt && JSON.stringify(cur) === JSON.stringify(f)) {
      return cur;   // preserve identity → no needless re-render / scroll reset
    }
    changed = true;
    return f;
  });
  if (!changed) {
    for (const t of current) if (!freshIds.has(t.id)) { changed = true; break; }   // a delete
  }
  return changed ? next : current;
}

// Defer remote refetches while the local user's own mutation is in flight, so a
// concurrent signal can't briefly revert their optimistic edit (D4 + Codex #9).
export function createSyncScheduler(refetch) {
  let pending = 0;
  let dirty = false;

  function flush() { dirty = false; refetch(); }

  return {
    // a remote 'tasks-changed' arrived
    onSignal() { if (pending > 0) dirty = true; else flush(); },
    // wrap a mutation promise so in-flight edits hold the refetch; a 15s safety
    // timeout means a hung/never-settling request can't starve remote updates.
    track(promise) {
      pending += 1;
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        pending = Math.max(0, pending - 1);
        if (pending === 0 && dirty) flush();
      };
      const t = setTimeout(done, INFLIGHT_SAFETY_MS);
      Promise.resolve(promise).then(() => {}, () => {}).finally(() => { clearTimeout(t); done(); });
      return promise;
    },
    _state() { return { pending, dirty }; }   // test hook
  };
}
